import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast, DirectoryCardSkeleton, PersonCard } from '@/components';
import { CACHE_KEYS } from '@/constants/cache-keys';
import { generatePath, useNavigate } from 'react-router-dom';
import { AppRoutes } from '@/constants/routes';
import { EngineService } from '@/services/engine';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import type { Person, UpdatePersonPayload } from '@/types/person';
import {
    getPersonMarkAsUnknownUpdate,
    markingAsUnknownRemovesTags,
} from '@/utils/person';
import { DirectoryGrid } from '@/features/DirectoryGrid';
import type { DirectoryGridConfig } from '@/features/DirectoryGrid';
import { MergePersonModal } from '@/features/MergeIdentity/person/MergePersonModal';
import { UpdatePersonModal } from './components/UpdatePersonModal';
import { DeletePersonConfirmationModal } from './components/DeletePersonConfirmationModal';
import { MarkPersonAsUnknownConfirmationModal } from './components/MarkPersonAsUnknownConfirmationModal';

const SORT_OPTIONS = [
    { value: 'date_newest', labelKey: 'browseBar.createdAtNewest' },
    { value: 'date_oldest', labelKey: 'browseBar.createdAtOldest' },
    { value: 'last_seen_newest', labelKey: 'browseBar.lastSeenNewest' },
    { value: 'last_seen_oldest', labelKey: 'browseBar.lastSeenOldest' },
    { value: 'name_a_z', labelKey: 'browseBar.nameAZ' },
    { value: 'name_z_a', labelKey: 'browseBar.nameZA' },
    { value: 'occurrences_high_low', labelKey: 'browseBar.occurrencesHighLow' },
    { value: 'occurrences_low_high', labelKey: 'browseBar.occurrencesLowHigh' },
];

const ORDER_BY_MAP: Record<string, string> = {
    date_newest: 'createdAt desc',
    date_oldest: 'createdAt asc',
    last_seen_newest: 'lastSeen desc',
    last_seen_oldest: 'lastSeen asc',
    name_a_z: 'name asc',
    name_z_a: 'name desc',
    occurrences_high_low: 'occurrences desc',
    occurrences_low_high: 'occurrences asc',
};

export function PersonGrid() {
    const { t } = useTranslation([
        I18nNamespace.DIRECTORY,
        I18nNamespace.COMMON,
    ]);
    const navigate = useNavigate();
    const [mergeSourcePerson, setMergeSourcePerson] = useState<Person | null>(
        null,
    );
    const [markAsUnknownRequest, setMarkAsUnknownRequest] = useState<{
        person: Person;
        confirm: () => void;
    } | null>(null);
    const revalidateRef = useRef<(() => Promise<void>) | null>(null);

    const openMergeModal = (person: Person) => {
        setMergeSourcePerson(person);
    };

    const config: DirectoryGridConfig<Person, UpdatePersonPayload> = {
        title: t('common:pageTitle.persons'),
        parentTitle: t('common:navigation.directory'),
        pageKey: 'persons',
        cacheKeyPrefix: CACHE_KEYS.ALL_PERSONS,
        statsCacheKey: CACHE_KEYS.ALL_PERSONS_STATS,
        fetchItems: (params) =>
            EngineService.getPersons({
                page: params.page,
                size: params.size,
                search: params.search,
                known: params.known,
                tags: params.tags,
                orderBy: params.orderBy,
            }),
        fetchStats: () => EngineService.getPersonsStats(),
        fetchTags: () => EngineService.getPersonTags(),
        tagsCacheKey: CACHE_KEYS.ALL_PERSONS_TAGS,
        vehicleTypeOptions: [],
        sortOptions: SORT_OPTIONS,
        defaultSort: 'last_seen_newest',
        orderByMap: ORDER_BY_MAP,
        itemRoute: AppRoutes.PERSON,
        routeParamKey: 'personId',
        searchPlaceholder: t('persons.searchPlaceholder'),
        tabLabels: {
            all: t('persons.tabs.all'),
            unknown: t('persons.tabs.unknown'),
            recognized: t('persons.tabs.recognized'),
        },
        messages: {
            empty: t('persons.noPersonsFound'),
            loadingMore: t('persons.loadingMore'),
            allLoaded: t('persons.allPersonsLoaded'),
            failedToLoad: t('persons.failedToLoad'),
        },
        selectionEntityLabel: t('common:pageTitle.persons'),
        bulkDeleteItems: (selection, filters) =>
            EngineService.deletePersons(selection, filters),
        canDeleteItem: (person) => !person.known,
        updateItem: (id, payload) => EngineService.updatePerson(id, payload),
        markAsUnknownUpdate: getPersonMarkAsUnknownUpdate,
        onUpdateSuccess: (mode) =>
            toast.success(
                t(
                    mode === 'recognize'
                        ? 'persons.recognizeModal.success'
                        : 'persons.editModal.success',
                ),
            ),
        onUpdateError: (mode) =>
            toast.error(
                t(
                    mode === 'recognize'
                        ? 'persons.recognizeModal.error'
                        : 'persons.editModal.error',
                ),
            ),
        onMarkAsUnknownSuccess: () =>
            toast.success(t('persons.markAsUnknown.success')),
        onMarkAsUnknownError: () =>
            toast.error(t('persons.markAsUnknown.error')),
        onDeleteSuccess: (deleted) =>
            toast.success(t('persons.deleteSuccess', { count: deleted })),
        onDeleteError: () => toast.error(t('persons.deleteError')),
        onPartialDelete: ({ deleted, failed }) =>
            toast.error(t('persons.deletePartial', { count: deleted, failed })),
        onDeleteInProgress: () => toast.info(t('persons.deleteInProgress')),
        onMergeSingleItem: openMergeModal,
    };

    return (
        <>
            <DirectoryGrid<Person, UpdatePersonPayload>
                config={config}
                renderCard={(person, handlers) => (
                    <PersonCard
                        key={person.id}
                        person={person}
                        checked={handlers.checked}
                        isDeleting={handlers.isDeleting}
                        selectionDisabled={handlers.selectionDisabled}
                        onClick={handlers.onClick}
                        onEdit={handlers.onEdit}
                        onDelete={handlers.onDelete}
                        onMarkAsRecognized={handlers.onMarkAsRecognized}
                        onMarkAsUnknown={(personId) => {
                            if (markingAsUnknownRemovesTags(person)) {
                                setMarkAsUnknownRequest({
                                    person,
                                    confirm: () =>
                                        handlers.onMarkAsUnknown(personId),
                                });
                                return;
                            }
                            handlers.onMarkAsUnknown(personId);
                        }}
                        onMerge={handlers.onMerge}
                    />
                )}
                renderEditModal={(props) => (
                    <UpdatePersonModal
                        isOpen={props.isOpen}
                        onClose={props.onClose}
                        isSubmitting={props.isSubmitting}
                        mode={props.mode}
                        person={props.item}
                        onUpdate={props.onUpdate}
                    />
                )}
                renderDeleteConfirmation={(props) => (
                    <DeletePersonConfirmationModal
                        isOpen={props.isOpen}
                        deleteInProgress={props.isDeleting}
                        count={props.count}
                        onConfirm={props.onConfirm}
                        onCancel={props.onCancel}
                    />
                )}
                skeletonComponent={<DirectoryCardSkeleton />}
                onRevalidateRef={revalidateRef}
            />
            <MarkPersonAsUnknownConfirmationModal
                isOpen={markAsUnknownRequest !== null}
                tags={markAsUnknownRequest?.person.tags ?? []}
                onConfirm={() => {
                    markAsUnknownRequest?.confirm();
                    setMarkAsUnknownRequest(null);
                }}
                onCancel={() => setMarkAsUnknownRequest(null)}
            />
            {mergeSourcePerson && (
                <MergePersonModal
                    isOpen={mergeSourcePerson !== null}
                    onClose={() => setMergeSourcePerson(null)}
                    variant="merge"
                    sourcePerson={mergeSourcePerson}
                    onSuccess={(result) => {
                        setMergeSourcePerson(null);
                        void revalidateRef.current?.();
                        navigate(
                            generatePath(AppRoutes.PERSON, {
                                personId: result.targetPerson.id,
                            }),
                        );
                    }}
                    onSourceStale={() => {
                        void revalidateRef.current?.();
                    }}
                />
            )}
        </>
    );
}
